import { BUILT_IN_NAMESPACES } from './built-in-tools/registry'
import { toolEntryName } from './tool-entries'

/**
 * Tool slots: a task names a tool by a slot it declares (`codehost`, `codehost.create_issue`) instead of by one
 * vendor's tool type, and each enterprise says which of its bindings fills that slot. So one task definition runs
 * against whichever system an enterprise connected (a code host, a chat tool, a tracker), and a team shared through
 * the catalog keeps working for an enterprise that connected a different vendor, without its tasks being rewritten.
 *
 * The OS knows nothing about what a slot means: a slot is a key the task's author chose, and a binding lists the keys
 * it fills (`config.slots`). A run substitutes the filling binding's tool type for the slot key everywhere the version
 * names a tool, so from there on the run behaves exactly as if the task had named that tool type: its credential, its
 * catalog, its must-gate list and its gates are the binding's own.
 *
 * Slot actions: two servers of one kind rarely name their tools alike (one code host merges with
 * `merge_pull_request`, another with `pull_request_write` and a `method`), so a task that names a tool under the key
 * (`codehost.merge_pull_request`) still runs against one vendor only. A slot may instead declare the actions the task
 * needs (`pr.merge`), the task names those (`codehost.pr.merge`), and the binding that fills the slot says which of its
 * tools each action is (`config.slotActions`). The run then maps each action to that server's own tool, pinned where
 * the map pins it, and everything downstream (the served tools, the gates, the must-gate list) sees the real name. The
 * OS defines no action and knows no vendor's names: the task declares what it needs, the binding says what it is called.
 */

/** One action a slot declares: the name the task uses under the slot key, and what it does, for a person mapping it. */
export type ToolSlotAction = {
    name: string
    description?: string | null
}

/** One slot a task version declares: the key its tools are named by, and how a person choosing a binding reads it. */
export type ToolSlot = {
    key: string
    label: string
    description?: string | null
    /**
     * The actions the task names under the key (`<key>.<action>`), each something any server of the slot's kind can
     * do. The filling binding maps each to one of its tools; a name under the key that is no declared action is the
     * filling server's own tool name, as every name under a key was before actions. Absent or empty declares none.
     */
    actions?: ToolSlotAction[] | null
}

/** A slot key reads like a tool type, since it stands where a tool type would: a letter, then letters, digits, `_` or `-`. */
export const TOOL_SLOT_KEY = /^[a-z][a-z0-9_-]{0,31}$/

/** An action name: lowercase words of letters, digits and `_`, joined by dots (`pr.merge`). Never a pin or a space. */
export const TOOL_SLOT_ACTION = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/

/** Most slots one task version declares, and most one binding fills. */
export const MAX_TOOL_SLOTS = 16

/** Most actions one slot declares. */
export const MAX_TOOL_SLOT_ACTIONS = 32

/** Longest label and description a declared slot carries. */
export const MAX_TOOL_SLOT_LABEL = 80
export const MAX_TOOL_SLOT_DESCRIPTION = 500

/** Longest description a declared action carries. */
export const MAX_TOOL_SLOT_ACTION_DESCRIPTION = 200

/**
 * A binding's action map (`config.slotActions`): each action name a task may use under a slot the binding fills, and
 * the tool it is on this binding's server, by the tool's own name with no prefix, pinned where one tool does several
 * things (`pull_request_write#method=merge`). One map per binding: a binding is one server, whichever slots it fills.
 */
export type SlotActionMap = Readonly<Record<string, string>>

/** A map target: a tool name as the server lists it, then an optional pin. Never a prefix: the binding's is added. */
export const SLOT_ACTION_TARGET = /^[A-Za-z0-9_-]+(#\S+)?$/

/** Most actions one binding maps. */
export const MAX_SLOT_ACTIONS = 64

/** The action maps of the bindings filling a run's slots, by slot key. */
export type SlotActionMaps = ReadonlyMap<string, SlotActionMap>

const NO_ACTIONS: SlotActionMaps = new Map()

/**
 * Prefixes a slot may never take: the OS's own namespaces, which the harness answers itself, so a slot of that name
 * could never be filled by a binding.
 */
export const RESERVED_TOOL_SLOT_KEYS: ReadonlySet<string> = new Set<string>(BUILT_IN_NAMESPACES)

/** Why a slot key cannot be used, or null when it can. */
export function toolSlotKeyProblem(key: string): 'tool_slot_key_invalid' | 'tool_slot_key_reserved' | null {
    if (!TOOL_SLOT_KEY.test(key)) {
        return 'tool_slot_key_invalid'
    }
    return RESERVED_TOOL_SLOT_KEYS.has(key) ? 'tool_slot_key_reserved' : null
}

/** The prefix a declared tool name selects its binding by: everything before the first `.` or `#`. */
export function toolNamePrefix(name: string): string {
    const end = name.search(/[.#]/)
    return end === -1 ? name : name.slice(0, end)
}

/**
 * The part of a declared name after its prefix, split at its pin: `.pr.merge#method=x` is `['pr.merge', '#method=x']`,
 * `.issue_read` is `['issue_read', '']`, and a bare key or a pinned bare key (`#x=y`) is `['', …]`.
 */
function restOf(rest: string): [string, string] {
    if (!rest.startsWith('.')) {
        return ['', rest]
    }
    const pinAt = rest.indexOf('#')
    return pinAt === -1 ? [rest.slice(1), ''] : [rest.slice(1, pinAt), rest.slice(pinAt)]
}

/**
 * The tool the part of a name after its slot key maps to through `map`: `.pr.merge` with
 * `{ "pr.merge": "pull_request_write#method=merge" }` is `pull_request_write#method=merge`. The map's own pin stands;
 * without one, the task's pin carries over. Null when the part names no mapped action, a bare key included.
 */
function actionTarget(rest: string, map: SlotActionMap | undefined): string | null {
    if (!map) {
        return null
    }
    const [action, pin] = restOf(rest)
    if (!action) {
        return null
    }
    const target = map[action]
    if (target === undefined) {
        return null
    }
    return target.includes('#') ? target : `${target}${pin}`
}

/**
 * A declared tool name with a filled slot's key replaced by the tool type that fills it: `codehost` becomes `github`,
 * `codehost.merge_pull_request#merge_method=squash` becomes `github.merge_pull_request#merge_method=squash`. With the
 * filling binding's action map, a declared action becomes that binding's tool for it: `codehost.pr.merge` becomes
 * `acmegit.pull_request_write#method=merge`. A name whose prefix is no filled slot is returned unchanged.
 */
export function fillToolName(
    name: string,
    fills: ReadonlyMap<string, string>,
    actions: SlotActionMaps = NO_ACTIONS,
): string {
    const prefix = toolNamePrefix(name)
    const filled = fills.get(prefix)
    if (filled === undefined) {
        return name
    }
    const rest = name.slice(prefix.length)
    const target = actionTarget(rest, actions.get(prefix))
    return target === null ? `${filled}${rest}` : `${filled}.${target}`
}

/**
 * A tools list (bare names or `{ name, … }` entries) with every filled slot replaced, every other key kept. Two
 * actions the map puts on one tool with different pins (`issue.create` → `issue_write#method=create`,
 * `issue.comment` → `issue_write#method=add_comment`) become one entry pinned to both values
 * (`issue_write#method=create,add_comment`), as a task bound to two methods of one tool declares it: left as two
 * entries, each pin would refuse the other's calls. A field one of them leaves open is left open.
 */
export function fillToolEntries<T>(
    entries: readonly T[],
    fills: ReadonlyMap<string, string>,
    actions: SlotActionMaps = NO_ACTIONS,
): T[] {
    if (fills.size === 0) {
        return [...entries]
    }
    const filled = entries.map((entry): { entry: T; mapped: boolean } => {
        if (typeof entry === 'string') {
            const name = fillToolName(entry, fills, actions)
            return { entry: name as T, mapped: isMappedName(entry, fills, actions) }
        }
        if (entry && typeof entry === 'object' && typeof (entry as { name?: unknown }).name === 'string') {
            const named = entry as T & { name: string }
            return {
                entry: { ...named, name: fillToolName(named.name, fills, actions) },
                mapped: isMappedName(named.name, fills, actions),
            }
        }
        return { entry, mapped: false }
    })
    return coalesceMapped(filled)
}

/** Whether a declared name is a slot action the filling binding's map names. */
function isMappedName(name: string, fills: ReadonlyMap<string, string>, actions: SlotActionMaps): boolean {
    const prefix = toolNamePrefix(name)
    return fills.has(prefix) && actionTarget(name.slice(prefix.length), actions.get(prefix)) !== null
}

/** The tool a filled name is, and its pin's fields: `acmegit.issue_write#method=a,b;x=y` → `['acmegit.issue_write', …]`. */
function splitPin(name: string): [string, Map<string, string[]>] {
    const at = name.indexOf('#')
    if (at === -1) {
        return [name, new Map()]
    }
    const fields = new Map<string, string[]>()
    for (const rule of name.slice(at + 1).split(';')) {
        const eq = rule.indexOf('=')
        if (eq <= 0) {
            continue
        }
        const values = rule
            .slice(eq + 1)
            .split(',')
            .map(value => value.trim())
            .filter(Boolean)
        if (values.length > 0) {
            fields.set(rule.slice(0, eq).trim(), values)
        }
    }
    return [name.slice(0, at), fields]
}

/** A pin from its fields, in the order given: `#method=a,b;x=y`; nothing for no fields. */
function joinPin(fields: ReadonlyMap<string, readonly string[]>): string {
    if (fields.size === 0) {
        return ''
    }
    return `#${[...fields].map(([field, values]) => `${field}=${values.join(',')}`).join(';')}`
}

/** `filled` with the mapped entries of one tool merged into the first of them, their pins unioned field by field. */
function coalesceMapped<T>(filled: ReadonlyArray<{ entry: T; mapped: boolean }>): T[] {
    const nameOf = (entry: T): string | null =>
        typeof entry === 'string' ? entry : (((entry as { name?: unknown } | null)?.name as string | undefined) ?? null)
    const groups = new Map<string, number[]>()
    filled.forEach(({ entry, mapped }, index) => {
        const name = mapped ? nameOf(entry) : null
        if (name !== null) {
            const [tool] = splitPin(name)
            groups.set(tool, [...(groups.get(tool) ?? []), index])
        }
    })
    const dropped = new Set<number>()
    const merged = new Map<number, string>()
    for (const [tool, indexes] of groups) {
        if (indexes.length < 2) {
            continue
        }
        const pins = indexes.map(index => splitPin(nameOf(filled[index]?.entry as T) ?? '')[1])
        const first = pins[0] as Map<string, string[]>
        const union = new Map<string, string[]>()
        for (const field of first.keys()) {
            // A field every entry pins is pinned to all their values; one any entry leaves open stays open.
            if (pins.every(pin => pin.has(field))) {
                union.set(field, [...new Set(pins.flatMap(pin => pin.get(field) ?? []))])
            }
        }
        merged.set(indexes[0] as number, `${tool}${joinPin(union)}`)
        for (const index of indexes.slice(1)) {
            dropped.add(index)
        }
    }
    return filled.flatMap(({ entry }, index) => {
        if (dropped.has(index)) {
            return []
        }
        const name = merged.get(index)
        if (name === undefined) {
            return [entry]
        }
        return [typeof entry === 'string' ? (name as T) : ({ ...(entry as object), name } as T)]
    })
}

/**
 * A gate or call-limit row with its tool filled. A pin on the filled name (the map's, or the task's own) becomes the
 * row's `match`, since a row is matched to a call by the tool's bare name and its payload: `codehost.pr.merge` with
 * `pr.merge → pull_request_write#method=merge` is the row `{ tool: "acmegit.pull_request_write", match: { method:
 * "merge" } }`. A field the row's own `match` already names keeps the row's value. A pin to several values cannot be
 * one `match` (an array there is a prefix), so that field is left out and the row covers every call of the tool.
 */
function fillRow<R extends { tool: string; match?: unknown }>(
    row: R,
    fills: ReadonlyMap<string, string>,
    actions: SlotActionMaps,
): R {
    const [tool, pinned] = splitPin(fillToolName(row.tool, fills, actions))
    if (pinned.size === 0) {
        return { ...row, tool }
    }
    const own =
        row.match && typeof row.match === 'object' && !Array.isArray(row.match)
            ? (row.match as Record<string, unknown>)
            : {}
    const match: Record<string, unknown> = { ...own }
    for (const [field, values] of pinned) {
        const name = field.replace(/^!/, '')
        if (values.length === 1 && !(name in match)) {
            match[name] = values[0]
        }
    }
    return { ...row, tool, ...(Object.keys(match).length > 0 ? { match } : {}) }
}

/** The fields of a task version that name tools, as a run reads them. */
export type ToolNamingFields = {
    tools?: readonly unknown[] | null
    gates?: ReadonlyArray<{ tool: string; match?: unknown }> | null
    workspace?: { credentials?: ReadonlyArray<{ tool: string }> | null } | null
    callLimits?: ReadonlyArray<{ tool: string; match?: unknown }> | null
    requiredWrites?: readonly string[] | null
}

/**
 * A version with its filled slots replaced in every field that names a tool: its tools, its gates, its workspace
 * credentials, its call limits and its required writes, each declared action mapped through the filling binding's
 * action map. A pin the mapping puts on a gate or call-limit row becomes its `match` (`fillRow`), and a required
 * write is the bare tool, since those rows are matched to a call by name. Every other field is kept as it is. With
 * nothing filled, the version is returned as it was.
 */
export function withFilledSlots<V extends ToolNamingFields>(
    version: V,
    fills: ReadonlyMap<string, string>,
    actions: SlotActionMaps = NO_ACTIONS,
): V {
    if (fills.size === 0) {
        return version
    }
    const workspace = version.workspace
    const bare = (name: string) => splitPin(fillToolName(name, fills, actions))[0]
    return {
        ...version,
        tools: version.tools ? fillToolEntries(version.tools, fills, actions) : version.tools,
        gates: version.gates ? version.gates.map(gate => fillRow(gate, fills, actions)) : version.gates,
        ...(version.callLimits ? { callLimits: version.callLimits.map(limit => fillRow(limit, fills, actions)) } : {}),
        ...(version.requiredWrites ? { requiredWrites: version.requiredWrites.map(bare) } : {}),
        workspace:
            workspace?.credentials && workspace.credentials.length > 0
                ? {
                      ...workspace,
                      credentials: workspace.credentials.map(credential => ({
                          ...credential,
                          tool: fills.get(credential.tool) ?? credential.tool,
                      })),
                  }
                : workspace,
    }
}

/** The keys a version declares as slots, each once. */
export function declaredSlotKeys(slots: readonly ToolSlot[] | null | undefined): string[] {
    return [...new Set((slots ?? []).map(slot => slot.key))]
}

/** Every tool name a version's naming fields hold, in order: what `withFilledSlots` would fill. */
function namedTools(version: ToolNamingFields): string[] {
    return [
        ...(version.tools ?? []).map(toolEntryName).filter((name): name is string => name !== null),
        ...(version.gates ?? []).map(gate => gate.tool),
        ...(version.callLimits ?? []).map(limit => limit.tool),
        ...(version.requiredWrites ?? []),
        ...(version.workspace?.credentials ?? []).map(credential => credential.tool),
    ]
}

/** A version's declared slots, with the fields `namedSlotActions` reads. */
export type SlotNamingFields = ToolNamingFields & { toolSlots?: readonly ToolSlot[] | null }

/**
 * The declared actions a version names under each slot that declares any, by key, each once in the order named:
 * `codehost.pr.merge` under a slot declaring `pr.merge` gives `codehost → ['pr.merge']`. A name under the key that is
 * no declared action is the filling server's own tool name and is not listed; a pin on a name is not part of the
 * action. A slot that declares no actions has no entry.
 */
export function namedSlotActions(version: SlotNamingFields): Map<string, string[]> {
    const declared = new Map<string, Set<string>>()
    for (const slot of version.toolSlots ?? []) {
        const names = (slot.actions ?? []).map(action => action.name)
        if (names.length > 0) {
            declared.set(slot.key, new Set(names))
        }
    }
    const named = new Map<string, string[]>()
    if (declared.size === 0) {
        return named
    }
    for (const name of namedTools(version)) {
        const prefix = toolNamePrefix(name)
        const actions = declared.get(prefix)
        if (!actions) {
            continue
        }
        const [action] = restOf(name.slice(prefix.length))
        if (!actions.has(action)) {
            continue
        }
        const list = named.get(prefix) ?? []
        if (!list.includes(action)) {
            list.push(action)
        }
        named.set(prefix, list)
    }
    return named
}

/**
 * The names a version uses under a slot that declares actions which are no declared action, as `<key>.<name>`: each is
 * the filling server's own tool name, so the task runs against that one vendor only. What a save warns about.
 */
export function undeclaredSlotActions(version: SlotNamingFields): string[] {
    const declared = new Map<string, Set<string>>()
    for (const slot of version.toolSlots ?? []) {
        const names = (slot.actions ?? []).map(action => action.name)
        if (names.length > 0) {
            declared.set(slot.key, new Set(names))
        }
    }
    const out: string[] = []
    for (const name of namedTools(version)) {
        const prefix = toolNamePrefix(name)
        const actions = declared.get(prefix)
        if (!actions) {
            continue
        }
        const [action] = restOf(name.slice(prefix.length))
        const named = action ? `${prefix}.${action}` : ''
        if (action && !actions.has(action) && !out.includes(named)) {
            out.push(named)
        }
    }
    return out
}

/**
 * The `<key>.<action>` names a run could not call: each declared action the version names under a filled slot that
 * the filling binding's map leaves out, in the order named. A slot nothing fills is not here: the preflight refuses
 * that on its own, as a tool nobody bound.
 */
export function unmappedSlotActions(
    version: SlotNamingFields,
    fills: ReadonlyMap<string, string>,
    actions: SlotActionMaps,
): string[] {
    const out: string[] = []
    for (const [slot, names] of namedSlotActions(version)) {
        if (!fills.has(slot)) {
            continue
        }
        const map = actions.get(slot) ?? {}
        for (const action of names) {
            if (map[action] === undefined) {
                out.push(`${slot}.${action}`)
            }
        }
    }
    return out
}

/**
 * What the task calls each tool a slot action maps to, for the model: the `<key>.<action>` names a version uses
 * under a filled slot, by the tool they become through the filling binding's map, without its pin
 * (`acmegit.pull_request_write` → `['codehost.pr.create', 'codehost.pr.merge']`). The skill names the action and the
 * model is served the binding's own name, so the served tool is told both. A tool no action maps to has no entry.
 */
export function slotActionAliases(
    version: SlotNamingFields,
    fills: ReadonlyMap<string, string>,
    actions: SlotActionMaps,
): Map<string, string[]> {
    const aliases = new Map<string, string[]>()
    for (const [slot, names] of namedSlotActions(version)) {
        const filled = fills.get(slot)
        const map = actions.get(slot)
        if (filled === undefined || !map) {
            continue
        }
        for (const action of names) {
            const target = map[action]
            if (target === undefined) {
                continue
            }
            const at = target.indexOf('#')
            const tool = `${filled}.${at === -1 ? target : target.slice(0, at)}`
            const list = aliases.get(tool) ?? []
            const alias = `${slot}.${action}`
            if (!list.includes(alias)) {
                list.push(alias)
            }
            aliases.set(tool, list)
        }
    }
    return aliases
}

/**
 * Whether a binding's list of repositories names `repo`. The list is comma, space or newline separated, each entry an
 * `owner/name` or an `owner/*` that names every repository of that owner. Case does not matter, and a trailing `.git`
 * is ignored on both sides. An empty list names nothing.
 */
export function repoListed(list: string | null | undefined, repo: string | null | undefined): boolean {
    const wanted = normaliseRepo(repo ?? '')
    if (!wanted) {
        return false
    }
    for (const raw of (list ?? '').split(/[\s,]+/)) {
        const entry = normaliseRepo(raw)
        if (!entry) {
            continue
        }
        if (entry === wanted) {
            return true
        }
        if (entry.endsWith('/*') && wanted.startsWith(entry.slice(0, -1))) {
            return true
        }
    }
    return false
}

function normaliseRepo(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/\.git$/, '')
        .replace(/^\/+|\/+$/g, '')
}
